import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  Switch,
  InputNumber,
  Spin,
  Row,
  Col,
  Space,
  Tag,
  Tooltip,
  Modal,
} from "antd";
import API from "../../../../services/api";
import { uploadToCloudinary, uploadMultipleToCloudinary } from "../../../../utils/uploadToCloudinary";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LocationField from "../../../LocationField";
import {
  EnvironmentOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CloseOutlined
} from "@ant-design/icons";
import "./EditCommercialSpace.css";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PROPERTY_TYPES = [
  "office",
  "shop",
  "warehouse",
  "showroom",
  "coworking",
  "other",
];

const AMENITIES_SUGGESTIONS = [
  "Parking",
  "Lift",
  "Security",
  "Power Backup",
  "AC",
  "Wi-Fi",
  "CCTV",
  "Fire Safety",
  "Waste Disposal",
  "Pantry",
  "Meeting Rooms",
  "Reception",
  "Signage",
  "Loading Dock",
  "Ventilation",
];

function EditCommercialSpace() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const furnished = Form.useWatch("furnished", form);
  const negotiable = Form.useWatch("negotiable", form);
  const amenitiesValue = Form.useWatch("amenities", form);
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [current, setCurrent] = useState(0);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [removedImages, setRemovedImages] = useState([]);
  const [stepChanging, setStepChanging] = useState(false);
  const [showCustomPropertyType, setShowCustomPropertyType] = useState(false);
  const [customPropertyType, setCustomPropertyType] = useState("");
  const abortControllerRef = useRef(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Debounce ref for amenity clicks
  const lastActionTimeRef = useRef({});

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getStepStyle = (step) => ({
    visibility: current === step ? "visible" : "hidden",
    position: current === step ? "relative" : "absolute",
    height: current === step ? "auto" : 0,

    /* IMPORTANT */
    overflow: current === step ? "visible" : "hidden",

    width: "100%",
    zIndex: current === step ? 100 : 0,
  });

  const steps = [
    "Basic Info",
    "Property Details",
    "Location",
    "Pricing",
    "Amenities",
    "Contact",
    "Images",
  ];

  const stepFields = [
    ["title", "description"],
    ["propertyType", "buildingType", "areaSqFt", "floor", "totalFloors", "propertyAge", "furnished"],
    ["address", "locality", "city", "state", "country", "pincode", "latitude", "longitude"],
    ["pricingType", "price", "deposit", "negotiable"],
    ["amenities"],
    ["contactName", "phone", "email"],
    ["thumbnail", "images"],
  ];

  const validateCurrentStep = async () => {
    const fieldsToValidate = stepFields[current];

    try {
      if (current === 1) {
        if (showCustomPropertyType && (!customPropertyType || customPropertyType.trim() === "")) {
          message.destroy();
          message.error("Please enter custom property type");
          return false;
        }
      }

      if (current === 6) {
        const thumbnailValue = form.getFieldValue("thumbnail");
        const imagesValue = form.getFieldValue("images");

        if (!thumbnailValue || thumbnailValue.length === 0) {
          message.destroy();
          message.error("Please upload a thumbnail image");
          return false;
        }

        if (!imagesValue || imagesValue.length === 0) {
          message.destroy();
          message.error("Please upload at least one image");
          return false;
        }
      }

      if (current === 4) {
        const amenitiesValue = form.getFieldValue("amenities");

        if (!amenitiesValue || amenitiesValue.trim() === "") {
          message.destroy();
          message.error("Please add at least one amenity");
          return false;
        }
      }

      if (current !== 4 && current !== 6) {
        await form.validateFields(fieldsToValidate);
      }
      return true;

    } catch (err) {
      let errorMessage = "Please fill all required fields";

      if (err?.errorFields?.length > 0) {
        errorMessage = err.errorFields[0]?.errors?.[0] || "Please fill all required fields correctly";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      message.destroy();
      message.error(errorMessage);
      return false;
    }
  };

  const next = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) return;

    setCurrent(current + 1);
    window.scrollTo(0, 0);
  };

  const prev = () => {
    setCurrent(prev => prev - 1);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setStepChanging(true);
      setCurrent(stepIndex);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setStepChanging(false);
      }, 100);
    }
  }, [current, steps.length]);

  const normalizeUpload = (e) => e?.fileList || [];

  const showFirstError = (err) => {
    const firstError =
      err?.errorFields?.[0]?.errors?.[0] || "Please fill the required field";
    message.error({ content: firstError, duration: 2.8 });
    if (err?.errorFields?.length) {
      form.scrollToField(err.errorFields[0].name, {
        block: "center",
        behavior: "smooth",
      });
    }
  };

  const sanitizeInput = (str) => {
    if (!str) return str;
    return str.replace(/[<>]/g, (char) => ({
      '<': '&lt;',
      '>': '&gt;'
    }[char]));
  };

  const createUploadFile = (file, index = 0) => {
    if (!file) return null;
    if (typeof file === "string") {
      if (file.startsWith('blob:')) {
        return {
          uid: `temp-${index}-${Date.now()}`,
          name: `temp-image-${index + 1}`,
          status: "done",
          url: file,
          originFileObj: null,
        };
      }
      return {
        uid: `existing-${index}-${Date.now()}`,
        name: `image-${index + 1}`,
        status: "done",
        url: file,
      };
    }
    return {
      uid: file._id || file.uid || `existing-${index}-${Date.now()}`,
      name: file.name || `image-${index + 1}`,
      status: "done",
      url: file.url || file.path || file.location || file.secure_url,
    };
  };

  const fetchCommercialSpace = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setPageLoading(true);
      setPageError(false);
      const res = await API.get(`/commercial-spaces/${id}`, {
        signal: abortControllerRef.current.signal
      });
      const item = res.data?.data || res.data;

      const thumbnailFileList = item?.thumbnail
        ? [createUploadFile(item.thumbnail, 0)].filter(Boolean)
        : [];

      const imagesFileList = Array.isArray(item?.images)
        ? item.images.map((img, index) => createUploadFile(img, index)).filter(Boolean)
        : [];

      let amenitiesValue = "";
      if (Array.isArray(item?.amenities)) {
        amenitiesValue = item.amenities
          .filter(a => typeof a === 'string')
          .join(", ");
      }

      form.setFieldsValue({
        title: item?.title || "",
        description: item?.description || "",
        propertyType: item?.propertyDetails?.propertyType || undefined,
        buildingType: item?.propertyDetails?.buildingType || "",
        areaSqFt: item?.propertyDetails?.areaSqFt ?? undefined,
        furnished: !!item?.propertyDetails?.furnished,
        floor: item?.propertyDetails?.floor ?? undefined,
        totalFloors: item?.propertyDetails?.totalFloors ?? undefined,
        propertyAge: item?.propertyDetails?.propertyAge ?? undefined,
        address: item?.location?.address || "",
        locality: item?.location?.locality || "",
        city: item?.location?.city || "",
        state: item?.location?.state || "",
        country: item?.location?.country || "",
        pincode: item?.location?.pincode || "",
        latitude: item?.location?.coordinates?.coordinates?.[1] ?? undefined,
        longitude: item?.location?.coordinates?.coordinates?.[0] ?? undefined,
        pricingType: item?.pricing?.type || undefined,
        price: item?.pricing?.price ?? undefined,
        deposit: item?.pricing?.deposit ?? undefined,
        negotiable: !!item?.pricing?.negotiable,
        amenities: amenitiesValue,
        contactName: item?.contactDetails?.contactName || "",
        phone: item?.contactDetails?.phone || "",
        email: item?.contactDetails?.email || "",
        thumbnail: thumbnailFileList,
        images: imagesFileList,
      });
      setIsFormDirty(false);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setPageError(true);
        message.error({
          content: err.response?.data?.message || "Failed to load commercial space details",
        });
      }
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCommercialSpace();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [id]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedCustomSelect = e.target.closest(
        ".ecom-custom-select"
      );

      if (!clickedCustomSelect) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty]);

  const getAmenitiesArray = () => {
    const value = amenitiesValue || "";
    return value
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean);
  };

  const handleAddAmenity = useCallback((amenity) => {
    const now = Date.now();
    const lastTime = lastActionTimeRef.current[`add-${amenity}`] || 0;
    if (now - lastTime < 200) return;
    lastActionTimeRef.current[`add-${amenity}`] = now;

    const currentValue = form.getFieldValue("amenities") || "";
    const amenitiesList = currentValue
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean);

    if (
      !amenitiesList.some(
        (a) => a.toLowerCase() === amenity.toLowerCase()
      )
    ) {
      amenitiesList.push(amenity);
      const newValue = amenitiesList.join(", ");
      form.setFieldsValue({ amenities: newValue });
    }
  }, [form]);

  const handleRemoveAmenity = useCallback((amenityToRemove) => {
    const now = Date.now();
    const lastTime = lastActionTimeRef.current[`remove-${amenityToRemove}`] || 0;
    if (now - lastTime < 500) return;
    lastActionTimeRef.current[`remove-${amenityToRemove}`] = now;

    const currentValue = form.getFieldValue("amenities") || "";
    const amenitiesList = currentValue
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean);
    const newList = amenitiesList.filter((a) => a !== amenityToRemove);
    const newValue = newList.join(", ");
    form.setFieldsValue({ amenities: newValue });


  }, [form]);

  const onFinish = async (values) => {
    if (!values.thumbnail || values.thumbnail.length === 0) {
      message.error({ content: "Please upload a thumbnail image before updating" });
      return;
    }

    try {
      setLoading(true);
      const data = {
        title: sanitizeInput(values.title),
        description: sanitizeInput(values.description),
        propertyDetails: {
          propertyType: values.propertyType,
          buildingType: sanitizeInput(values.buildingType),
          areaSqFt: Number(values.areaSqFt),
          furnished: !!values.furnished,
          floor: values.floor !== undefined ? Number(values.floor) : 0,
          totalFloors: values.totalFloors !== undefined ? Number(values.totalFloors) : 1,
          propertyAge: values.propertyAge !== undefined ? Number(values.propertyAge) : 0,
        },
        location: {
          address: sanitizeInput(values.address),
          city: sanitizeInput(values.city),
          locality: sanitizeInput(values.locality),
          state: sanitizeInput(values.state),
          country: sanitizeInput(values.country),
          pincode: values.pincode,
          coordinates: {
            type: "Point",
            coordinates: [Number(values.longitude), Number(values.latitude)],
          },
        },
        pricing: {
          type: values.pricingType,
          price: Number(values.price),
          deposit: values.deposit !== undefined ? Number(values.deposit) : 0,
          negotiable: !!values.negotiable,
        },
        amenities: values.amenities
          ? values.amenities.split(",").map((a) => a.trim()).filter(Boolean).map(a => sanitizeInput(a))
          : [],
        contactDetails: {
          contactName: sanitizeInput(values.contactName),
          phone: values.phone,
          email: values.email,
        },
        existingThumbnail:
          values.thumbnail?.[0] && !values.thumbnail[0].originFileObj
            ? values.thumbnail[0].url
            : null,
        existingImages:
          values.images
            ?.filter((file) => !file.originFileObj)
            .map((file) => file.url)
            .filter(Boolean) || [],
        deleteImages: removedImages,
      };

      // ✅ Upload NEW images to Cloudinary directly from browser
      const uploadedThumb = values.thumbnail?.[0]?.originFileObj
        ? await uploadToCloudinary(values.thumbnail[0].originFileObj, "commercial_spaces")
        : null;

      const uploadedImages = values.images?.some(f => f.originFileObj)
        ? await uploadMultipleToCloudinary(
          values.images.map(f => f.originFileObj).filter(Boolean),
          "commercial_spaces"
        )
        : [];

      await API.put(`/commercial-spaces/${id}`, {
        ...data,

        newThumbnail: uploadedThumb?.url || uploadedThumb,

        newImages: uploadedImages.map(img => img.url || img),
      });
      setIsFormDirty(false);
      message.success({ content: "Commercial space updated successfully" });

      navigate("/my-commercial-spaces");
    } catch (err) {
      message.error({
        content:
          err.response?.data?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    showFirstError(errorInfo);
  };

  const handleBack = () => {
    if (isFormDirty) {
      setShowUnsavedModal(true);
    } else {
      navigate("/my-commercial-spaces");
    }
  };

  const handleKeyDown = (e, callback) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  const isMobile = windowWidth <= 768;

  if (!pageLoading && pageError) {
    return (
      <div className="ecom-page">
        <div className="ecom-container">
          <Card className="ecom-card">
            <div className="ecom-error-state">
              <ExclamationCircleOutlined className="ecom-error-icon" />
              <h2>Failed to load property details</h2>
              <p>Something went wrong while fetching the data. Please try again.</p>
              <Button
                type="primary"
                className="ecom-btn-next"
                onClick={fetchCommercialSpace}
              >
                Retry
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="ecom-page">
      <div className="ecom-container">
        <Spin spinning={pageLoading || stepChanging} description={stepChanging ? "Loading step..." : "Loading property details..."}>
          <Modal
            title={
              <Space>
                <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                <span>Unsaved Changes</span>
              </Space>
            }
            open={showUnsavedModal}
            onOk={() => {
              setShowUnsavedModal(false);
              navigate("/my-commercial-spaces");
            }}
            onCancel={() => setShowUnsavedModal(false)}
            okText="Leave"
            cancelText="Stay and Continue Editing"
            okButtonProps={{ danger: true }}
            className="ecom-unsaved-modal"
            width={isMobile ? "90%" : 520}
          >
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <Text type="secondary">
              Your changes will be lost if you don't save them.
            </Text>
          </Modal>

          <Card className="ecom-card" variant={false}>
            <div className="ecom-card-header">
              <div className="ecom-card-header-left">
                <Tag className="ecom-edit-badge">Editing Mode</Tag>
                <h1 className="ecom-title">Edit Commercial Space</h1>
                <p className="ecom-subtitle">Update your property listing details</p>
              </div>
              <Button
                icon={<CloseOutlined />}
                className="ecom-back-btn"
                onClick={handleBack}
                aria-label="Go back"
              >
                {!isMobile && "Back to Listing"}
              </Button>
            </div>

            <div className="ecom-stepper" role="list">
              {steps.map((step, i) => (
                <div
                  key={step}
                  className={`ecom-step ${current === i ? "active" : ""} ${current > i ? "done" : ""
                    }`}
                  onClick={() => {
                    goToStep(i);
                  }}
                  onKeyDown={(e) => {
                    handleKeyDown(e, () => goToStep(i));
                  }}
                  role="listitem"
                  aria-label={`Step ${i + 1}: ${step}${current > i ? " (completed)" : current === i ? " (current)" : ""
                    }`}
                  tabIndex={0}
                  data-step={i}
                >
                  <div className="ecom-step-line-left" />
                  <div className="ecom-step-inner">
                    <div className="ecom-circle">
                      {current > i ? <CheckCircleOutlined /> : i + 1}
                    </div>
                    <span className="ecom-step-label">{step}</span>
                  </div>
                  <div className="ecom-step-line-right" />
                </div>
              ))}
            </div>

            <div className="ecom-progress-bar">
              <div
                className="ecom-progress-fill"
                style={{ width: `${(current / (steps.length - 1)) * 100}%` }}
              />
            </div>
            <div className="ecom-progress-label">
              <Space>
                <span>
                  Step {current + 1} of {steps.length}
                </span>
                {!isMobile && (
                  <span className="ecom-progress-step-name">
                    — {steps[current]}
                  </span>
                )}
              </Space>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              onValuesChange={() => setIsFormDirty(true)}
              validateTrigger="onBlur"
              scrollToFirstError
              className="ecom-form"
            >
              <div style={getStepStyle(0)}>
                <div className="ecom-step-heading">
                  <h2>Basic Information</h2>
                  <p>Core details about your property</p>
                </div>

                <Form.Item
                  name="title"
                  label="Property Title"
                  rules={[
                    { required: true, message: "Please enter a title for your property" },
                    { min: 5, message: "Title must be at least 5 characters long" },
                    {
                      pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.#&-]*$/,
                      message: "Title must include at least one letter and can contain numbers or symbols like # . & -",
                    },
                  ]}
                >
                  <Input placeholder="e.g. Premium Office Space in Indiranagar" />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    { required: true, message: "Please provide a description" },
                    { min: 20, message: "Description should be at least 20 characters" },
                  ]}
                >
                  <TextArea rows={5} placeholder="Describe the key features, location advantages, and unique qualities of your commercial space..." />
                </Form.Item>
              </div>

              <div style={getStepStyle(1)}>
                <div className="ecom-step-heading">
                  <h2>Property Details</h2>
                  <p>Structural and classification information</p>
                </div>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="propertyType"
                      label="Property Type"
                      rules={[{ required: true, message: "Please select a property type" }]}
                    >
                      <div className="ecom-custom-select">
                        <div
                          className="ecom-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === "propertyType"
                                ? null
                                : "propertyType"
                            )
                          }
                        >
                          {form.getFieldValue("propertyType")
                            ? form
                              .getFieldValue("propertyType")
                              .charAt(0)
                              .toUpperCase() +
                            form
                              .getFieldValue("propertyType")
                              .slice(1)
                            : "Select property type"}

                          <span className="ecom-custom-arrow">⌄</span>
                        </div>

                        {openDropdown === "propertyType" && (
                          <div className="ecom-custom-dropdown">
                            {PROPERTY_TYPES.map((item) => (
                              <div
                                key={item}
                                className="ecom-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "propertyType",
                                    item
                                  );

                                  if (item === "other") {
                                    setShowCustomPropertyType(true);
                                  } else {
                                    setShowCustomPropertyType(false);
                                    setCustomPropertyType("");
                                  }

                                  setOpenDropdown(null);
                                }}
                              >
                                {item.charAt(0).toUpperCase() +
                                  item.slice(1)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Form.Item>
                    {showCustomPropertyType && (
                      <Form.Item
                        name="customPropertyType"
                        label="Enter Custom Property Type"
                        rules={[
                          { required: true, message: "Please enter custom property type" },
                          { min: 2, message: "Property type must be at least 2 characters" },
                          { pattern: /^[A-Za-z][A-Za-z\s&-]*$/, message: "Start with letter, can include letters, spaces, & and -" }
                        ]}
                        style={{ marginTop: -8 }}
                      >
                        <Input
                          placeholder="e.g. Restaurant, Hotel, Clinic"
                          value={customPropertyType}
                          onChange={(e) => {
                            setCustomPropertyType(e.target.value);
                            form.setFieldValue("propertyType", e.target.value);
                          }}
                        />
                      </Form.Item>
                    )}
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="buildingType"
                      label="Building Name"
                      rules={[
                        { required: true, message: "Please enter the building name" },
                        {
                          pattern: /^(?=.*[A-Za-z0-9])[A-Za-z0-9\s.-]+$/,
                          message: "Building name can include letters, numbers, spaces, . and -",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. Prestige Tech Park" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="floor"
                      label="Floor Number"
                      dependencies={["totalFloors"]}
                      rules={[
                        { required: true, message: "Please enter the floor number" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const totalFloors = getFieldValue("totalFloors");
                            if (value && totalFloors && value > totalFloors) {
                              return Promise.reject(new Error("Floor cannot exceed total floors"));
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 3" min={0} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="totalFloors"
                      label="Total Floors"
                      rules={[{ required: true, message: "Please enter total floors" }]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 10" min={1} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="propertyAge"
                      label="Property Age (Years)"
                      rules={[
                        { required: true, message: "Please enter property age" },
                        { type: "number", min: 0, message: "Property age cannot be negative" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 5" min={0} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="areaSqFt"
                      label="Area (sq ft)"
                      rules={[
                        { required: true, message: "Please enter the area in sq ft" },
                        { type: "number", min: 10, message: "Area must be at least 10 sq ft" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 1200" min={10} />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <div className="ecom-switch-wrapper">
                      <Form.Item label="Furnished Status" className="ecom-switch-item">
                        <Form.Item name="furnished" valuePropName="checked" noStyle>
                          <Switch
                            checkedChildren="Furnished"
                            unCheckedChildren="Unfurnished"
                          />
                        </Form.Item>
                        <span className="ecom-switch-label">
                          {furnished
                            ? "Space comes with furniture"
                            : "Empty space, no furniture included"}
                        </span>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </div>

              <div style={getStepStyle(2)}>
                <div className="ecom-step-heading">
                  <h2>Location Details</h2>
                  <p>Address and geolocation coordinates</p>
                </div>

                <Form.Item
                  name="address"
                  label="Address"
                  rules={[{ required: true, message: "Please enter the Address" }]}
                >
                  <Input placeholder="e.g. 45, 1st Cross, Indiranagar, Bengaluru" />
                </Form.Item>

                <Form.Item
                  name="locality"
                  label="Locality"
                  rules={[
                    { required: true, message: "Please enter locality" },
                    { min: 2, message: "Minimum 2 characters" },
                    {
                      pattern: /^[A-Za-z0-9\s().-]*$/,
                      message:
                        "Locality can only contain letters, numbers, spaces, (), . and -",
                    },
                  ]}
                >
                  <LocationField
                    form={form}
                    message={message}
                    setAutoDetecting={setAutoDetecting}
                  />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="city"
                      label="City"
                      rules={[
                        { required: true, message: "Please enter the city" },
                        { pattern: /^[A-Za-z\s.-]+$/, message: "City name should contain only letters" },
                      ]}
                    >
                      <Input placeholder="e.g. Bengaluru" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="state"
                      label="State"
                      rules={[
                        { required: true, message: "Please enter the State" },
                        { pattern: /^[A-Za-z\s.-]+$/, message: "State name should contain only letters" },
                      ]}
                    >
                      <Input placeholder="e.g. Karnataka" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="country"
                      label="Country"
                      rules={[
                        { required: true, message: "Please enter the country" },
                        { pattern: /^[A-Za-z\s.-]+$/, message: "Country name should contain only letters" },
                      ]}
                    >
                      <Input placeholder="e.g. India" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="pincode"
                      label="Pincode"
                      rules={[
                        { required: true, message: "Please enter the pincode" },
                        { pattern: /^[1-9][0-9]{5}$/, message: "Enter a valid 6-digit pincode" },
                      ]}
                    >
                      <Input maxLength={6} placeholder="e.g. 560038" style={{ maxWidth: 200 }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="latitude"
                      label="Latitude"
                      rules={[
                        { required: true, message: "Latitude is required" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "") {
                              return Promise.reject("Latitude is required");
                            }
                            const numValue = Number(value);
                            if (isNaN(numValue)) {
                              return Promise.reject("Enter a valid latitude");
                            }
                            const str = value.toString();
                            if ((str.match(/\./g) || []).length > 1) {
                              return Promise.reject("Only one decimal point allowed");
                            }
                            const parts = str.split(".");
                            if (parts[1] && parts[1].length > 6) {
                              return Promise.reject("Max 6 digits allowed after decimal");
                            }
                            if (numValue < -90 || numValue > 90) {
                              return Promise.reject("Latitude must be between -90 and 90");
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-90}
                        max={90}
                        step={0.000001}
                        placeholder="e.g. 12.971891"
                        onBlur={() => {
                          const val = form.getFieldValue("latitude");
                          if (typeof val === "number" && !isNaN(val)) {
                            form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="longitude"
                      label="Longitude"
                      rules={[
                        { required: true, message: "Longitude is required" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "") {
                              return Promise.reject("Longitude is required");
                            }
                            const numValue = Number(value);
                            if (isNaN(numValue)) {
                              return Promise.reject("Enter a valid longitude");
                            }
                            const str = value.toString();
                            if ((str.match(/\./g) || []).length > 1) {
                              return Promise.reject("Only one decimal point allowed");
                            }
                            const parts = str.split(".");
                            if (parts[1] && parts[1].length > 6) {
                              return Promise.reject("Max 6 digits allowed after decimal");
                            }
                            if (numValue < -180 || numValue > 180) {
                              return Promise.reject("Longitude must be between -180 and 180");
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-180}
                        max={180}
                        step={0.000001}
                        placeholder="e.g. 77.594563"
                        onBlur={() => {
                          const val = form.getFieldValue("longitude");
                          if (typeof val === "number" && !isNaN(val)) {
                            form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div style={getStepStyle(3)}>
                <div className="ecom-step-heading">
                  <h2>Pricing Information</h2>
                  <p>Commercial terms and financial details</p>
                </div>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="pricingType"
                      label="Pricing Type"
                      rules={[{ required: true, message: "Please select a pricing type" }]}
                    >
                      <div className="ecom-custom-select">
                        <div
                          className="ecom-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === "pricingType"
                                ? null
                                : "pricingType"
                            )
                          }
                        >
                          {form.getFieldValue("pricingType")
                            ? form
                              .getFieldValue("pricingType")
                              .charAt(0)
                              .toUpperCase() +
                            form
                              .getFieldValue("pricingType")
                              .slice(1)
                            : "Select pricing type"}

                          <span className="ecom-custom-arrow">⌄</span>
                        </div>

                        {openDropdown === "pricingType" && (
                          <div className="ecom-custom-dropdown">
                            {[
                              { label: "Rent", value: "rent" },
                              { label: "Lease", value: "lease" },
                              { label: "Sale", value: "sell" },
                            ].map((item) => (
                              <div
                                key={item.value}
                                className="ecom-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "pricingType",
                                    item.value
                                  );

                                  if (item.value !== "rent") {
                                    form.setFieldsValue({
                                      deposit: undefined,
                                    });
                                  }

                                  setOpenDropdown(null);
                                }}
                              >
                                {item.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="price"
                      label="Price (₹)"
                      rules={[
                        { required: true, message: "Please enter the price" },
                        { type: "number", min: 1, message: "Price must be greater than 0" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 50000" min={1} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item shouldUpdate noStyle>
                      {() =>
                        form.getFieldValue("pricingType") === "rent" && (
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name="deposit"
                              label="Deposit (₹)"
                              dependencies={["price", "pricingType"]}
                              rules={[
                                { required: true, message: "Please enter the deposit amount" },
                                { type: "number", min: 0, message: "Deposit cannot be negative" },
                                {
                                  validator: (_, value) => {
                                    const price = form.getFieldValue("price");

                                    if (
                                      value !== undefined &&
                                      price !== undefined &&
                                      value <= price
                                    ) {
                                      return Promise.reject(
                                        new Error("Deposit must be greater than price")
                                      );
                                    }

                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <InputNumber
                                style={{ width: "100%" }}
                                placeholder="Deposit amount"
                                min={0}
                                formatter={(value) =>
                                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }
                                parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                              />
                            </Form.Item>
                          </Col>
                        )
                      }
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <div className="ecom-switch-wrapper">
                      <Form.Item label="Price Negotiable" className="ecom-switch-item">
                        <Form.Item name="negotiable" valuePropName="checked" noStyle>
                          <Switch checkedChildren="Yes" unCheckedChildren="No" />
                        </Form.Item>
                        <span className="ecom-switch-label">
                          {negotiable
                            ? "Price is open to negotiation"
                            : "Price is fixed"}
                        </span>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </div>

              <div style={getStepStyle(4)}>
                <div className="ecom-step-heading">
                  <h2>Amenities</h2>
                  <p>List all available amenities (comma-separated)</p>
                </div>

                <div className="ecom-amenities-suggestions">
                  <Text className="ecom-suggestions-label">Quick add amenities:</Text>
                  <div className="ecom-suggestions-tags">
                    {AMENITIES_SUGGESTIONS.map((amenity) => {
                      const isAdded = getAmenitiesArray().includes(amenity);
                      return (
                        <Tag
                          key={amenity}
                          className={`ecom-suggestion-tag ${isAdded ? "added" : ""}`}
                          onClick={() => {
                            if (isAdded) {
                              handleRemoveAmenity(amenity);
                            } else {
                              handleAddAmenity(amenity);
                            }
                          }}
                          onKeyDown={(e) => handleKeyDown(e, () => {
                            if (isAdded) {
                              handleRemoveAmenity(amenity);
                            } else {
                              handleAddAmenity(amenity);
                            }
                          })}
                          style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                          tabIndex={0}
                          role="button"
                          aria-label={`${isAdded ? "Remove" : "Add"} ${amenity} amenity`}
                        >
                          {isAdded ? <CheckCircleOutlined /> : <PlusOutlined />}
                          {amenity}
                        </Tag>
                      );
                    })}
                  </div>
                </div>

                <Form.Item
                  name="customAmenity"
                  label="Custom Amenities"
                >
                  <Space.Compact style={{ width: "100%" }}>
                    <Input

                      placeholder="Enter custom amenity"
                      onChange={(e) => {
                        form.setFieldsValue({
                          customAmenity: e.target.value,
                        });
                      }}
                      onPressEnter={(e) => {
                        e.preventDefault();

                        const value = form.getFieldValue("customAmenity");

                        if (!value || !value.trim()) return;

                        handleAddAmenity(value.trim());

                        form.resetFields(["customAmenity"]);
                      }}
                    />
                    <Button
                      type="primary"
                      className="ecom-add-amenity-btn"
                      onClick={() => {
                        const value = form.getFieldValue("customAmenity");

                        if (!value || !value.trim()) return;

                        handleAddAmenity(value.trim());

                        form.resetFields(["customAmenity"]);
                      }}
                    >
                      Add
                    </Button>
                  </Space.Compact>
                </Form.Item>

                <Form.Item
                  name="amenities"
                  hidden
                >
                  <Input />
                </Form.Item>

                {getAmenitiesArray().length > 0 && (
                  <div className="ecom-current-amenities">
                    <Text className="ecom-current-label">Current amenities:</Text>
                    <div className="ecom-amenities-list">
                      {getAmenitiesArray().map((amenity, index) => (
                        <Tag
                          key={`${amenity}-${index}`}
                          closable
                          onClose={(e) => {
                            e.preventDefault();
                            handleRemoveAmenity(amenity);
                          }}
                          className="ecom-amenity-tag"
                          style={{ cursor: "pointer" }}
                        >
                          {amenity}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={getStepStyle(5)}>
                <div className="ecom-step-heading">
                  <h2>Contact Information</h2>
                  <p>Primary contact for enquiries</p>
                </div>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="contactName"
                      label="Contact Person"
                      rules={[
                        { required: true, message: "Please enter contact name" },
                        { min: 2, message: "Please enter a valid name" },
                        {
                          pattern: /^[A-Za-z\s.-]+$/,
                          message:
                            "Name can only contain letters and basic characters",
                        },
                      ]}
                    >
                      <Input placeholder="Full name" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[
                        { required: true, message: "Please enter a phone number" },
                        {
                          pattern: /^[6-9]\d{9}$/,
                          message: "Enter a valid 10-digit Indian phone number",
                        },
                      ]}
                    >
                      <Input maxLength={10} placeholder="9876543210" />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[
                        { required: true, message: "Please enter your email address" },
                        { type: "email", message: "Enter a valid email address" },
                        {
                          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Enter a valid email address",
                        },
                      ]}
                    >
                      <Input placeholder="example@email.com" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div style={getStepStyle(6)}>
                <div className="ecom-step-heading">
                  <h2>Property Images</h2>
                  <p>Thumbnail (1 image) and Gallery (up to 5 images)</p>
                </div>

                <Form.Item
                  name="thumbnail"
                  label={
                    <div className="ecom-label-with-badge">
                      Thumbnail Image
                      <Tag color="blue" className="ecom-badge-primary">
                        Main Image
                      </Tag>
                    </div>
                  }
                  valuePropName="fileList"
                  getValueFromEvent={normalizeUpload}
                  rules={[
                    { required: true, message: "Please upload a thumbnail image" },
                    {
                      validator: (_, value) => {
                        if (!value || value.length === 0) {
                          return Promise.reject(
                            new Error("Please upload a thumbnail image")
                          );
                        }
                        if (value.length > 1) {
                          return Promise.reject(
                            new Error("Only one thumbnail image is allowed")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Upload.Dragger
                    maxCount={1}
                    accept="image/png,image/jpeg,image/webp"
                    className="ecom-upload"
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(
                        file.type
                      );

                      if (!isValidType) {
                        message.error({ content: "Only PNG, JPEG allowed" });
                        return Upload.LIST_IGNORE;
                      }

                      return false;
                    }}
                  >
                    <div className="ecom-upload-inner">
                      {/* <div className="ecom-upload-icon">📷</div> */}
                      <p className="ecom-upload-title">
                        Click or drag to upload thumbnail
                      </p>
                      <p className="ecom-upload-hint">PNG, JPEG</p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>

                <Form.Item
                  name="images"
                  valuePropName="fileList"
                  label={
                    <div className="ecom-label-with-badge">
                      <span style={{ color: '#ff4d4f' }}>*</span>Gallery Images
                      <Tag color="default" className="ecom-badge-secondary">
                        Up to 5
                      </Tag>
                    </div>
                  }
                  getValueFromEvent={normalizeUpload}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || value.length === 0) {
                          return Promise.reject(
                            new Error(
                              "Please upload at least one image of the property"
                            )
                          );
                        }
                        if (value.length > 5) {
                          return Promise.reject(
                            new Error("Maximum 5 images allowed")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Upload.Dragger
                    className="ecom-upload"
                    maxCount={5}
                    accept="image/png,image/jpeg"
                    multiple
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(
                        file.type
                      );

                      if (!isValidType) {
                        message.error({ content: "Only PNG, JPEG allowed" });
                        return Upload.LIST_IGNORE;
                      }

                      return false;
                    }}
                    onRemove={(file) => {
                      if (!file.originFileObj && file.url) {
                        setRemovedImages((prev) => [...prev, file.url]);
                      }
                    }}
                  >
                    <div className="ecom-upload-inner">
                      {/* <div className="ecom-upload-icon">🖼️</div> */}
                      <p className="ecom-upload-title">
                        Click or drag to upload gallery images
                      </p>
                      <p className="ecom-upload-hint">
                        Up to 5 images · PNG, JPEG
                      </p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>
              </div>

              <div className="ecom-btns">
                <div className="ecom-btns-left">
                  {current > 0 && (
                    <Button
                      onClick={prev}
                      className="ecom-btn-prev"
                      icon={<ArrowLeftOutlined />}
                      disabled={autoDetecting}
                    >
                      Back
                    </Button>
                  )}
                </div>
                <div className="ecom-btns-right">
                  {current < steps.length - 1 && (
                    <Button
                      type="primary"
                      onClick={next}
                      className="ecom-btn-next"
                      disabled={autoDetecting}
                    >
                      Save & Continue
                    </Button>
                  )}
                  {current === steps.length - 1 && (
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      disabled={loading}
                      icon={<SaveOutlined />}
                      className="ecom-btn-submit"
                    >
                      {loading ? "Updating..." : "Update Listing"}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </Card>
        </Spin>
      </div>
    </div>
  );
}

export default EditCommercialSpace;